import { redirect } from "@tanstack/react-router";

import { loadPartnerSession, sessionPermissions, type PartnerSession } from "./auth";

export type PermissionRequirement = string | readonly string[];
type InternalRoute =
  | "/app/overview"
  | "/app/opportunities"
  | "/app/adjustments"
  | "/app/account-mapping"
  | "/app/company-setup"
  | "/app/audit-history"
  | "/app/connections"
  | "/app/people-access"
  | "/app/source-reconciliation";

const routePermissions: Array<{
  route: InternalRoute;
  requirement: PermissionRequirement;
}> = [
  { route: "/app/overview", requirement: "reporting.overview.view" },
  { route: "/app/opportunities", requirement: "opportunities.view" },
  { route: "/app/adjustments", requirement: "adjustments.view" },
  { route: "/app/account-mapping", requirement: "account_mapping.view" },
  {
    route: "/app/company-setup",
    requirement: ["companies.manage", "reporting_entities.manage", "operating_locations.manage"],
  },
  { route: "/app/audit-history", requirement: "audit.view" },
  { route: "/app/connections", requirement: "connections.view" },
  { route: "/app/people-access", requirement: "users.view" },
  { route: "/app/source-reconciliation", requirement: "finance_tools.view" },
];

export function hasRoutePermission(
  session: PartnerSession,
  requirement: PermissionRequirement,
) {
  const permissions = sessionPermissions(session);
  return typeof requirement === "string"
    ? permissions.has(requirement)
    : requirement.some((permission) => permissions.has(permission));
}

export function defaultInternalRoute(session: PartnerSession): InternalRoute {
  return routePermissions.find(({ requirement }) => hasRoutePermission(session, requirement))?.route
    ?? "/app/overview";
}

export async function requireInternalSession() {
  const session = await loadPartnerSession();
  if (session.audience === "company_customer") throw redirect({ to: "/customer" });
  return session;
}

export async function requireCustomerSession() {
  const session = await loadPartnerSession();
  if (session.audience !== "company_customer") throw redirect({ to: defaultInternalRoute(session) });
  return session;
}

export async function requireRoutePermission(requirement: PermissionRequirement) {
  const session = await requireInternalSession();
  if (!hasRoutePermission(session, requirement)) throw redirect({ to: defaultInternalRoute(session) });
  return session;
}
